import React from "react";
import { useDrag, useDrop } from "react-dnd";
import { FaPlay } from "react-icons/fa6";
import { RiDeleteBinLine } from "react-icons/ri";
import VideoPreviewModal from "./Video/VideoPreviewModal";
import { FILE_TYPE_ID, VIDEO_STATUS } from "../lib/constants";
import { toast } from "../@/components/ui/use-toast";
import { HLSPlayer } from "./Video/HLSPlayer";
import { replaceVideoUrl } from "../lib/utils";

interface MediaItemProps {
  file?: File;
  preview: string;
  index: number;
  moveImage: (dragIndex: number, hoverIndex: number) => void;
  removeImage: (index: number) => void;
  fileType: number;
  statusId?: number;
  uploadProgress?: { progress: number; status: string };
}

const MediaItem: React.FC<MediaItemProps> = ({
  file,
  preview,
  index,
  moveImage,
  removeImage,
  fileType,
  statusId,
  uploadProgress,
}) => {
  const [, ref] = useDrag({
    type: "image",
    item: { index },
  });
  const [isPreviewOpen, setIsPreviewOpen] = React.useState(false);
  const filePath = file ? URL.createObjectURL(file) : preview;

  const [, drop] = useDrop({
    accept: "image",
    hover(item: { index: number }) {
      if (item.index !== index) {
        moveImage(item.index, index);
        item.index = index;
      }
    },
  });

  const handlePreviewClick = () => {
    if (
      fileType === FILE_TYPE_ID.VIDEO &&
      uploadProgress?.status === "uploading"
    ) {
      toast({
        title: "Video Upload in Progress",
        description:
          "Please wait for the video to finish uploading before previewing.",
      });
      return;
    }
    setIsPreviewOpen(true);
  };

  const renderPreview = () => {
    if (fileType === FILE_TYPE_ID.VIDEO) {
      return (
        <div
          className={`relative w-32 h-32 ${
            uploadProgress?.status === "uploading" ? "opacity-50" : ""
          }`}
          onClick={handlePreviewClick}
        >
          <video
            className="w-full h-full object-cover rounded-lg cursor-pointer"
            src={replaceVideoUrl(preview)}
          >
            Your browser does not support the video tag.
          </video>
          {uploadProgress?.status === "uploading" && (
            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 rounded-lg">
              <span className="text-white text-sm">
                Uploading... {Math.round(uploadProgress.progress)}%
              </span>
            </div>
          )}
        </div>
      );
    }
    return (
      <div
        className={`relative w-32 h-32 ${
          uploadProgress?.status === "uploading" ? "opacity-50" : ""
        }`}
        onClick={handlePreviewClick}
      >
        <img
          src={preview}
          alt="Uploaded preview"
          className="w-full h-full object-cover rounded-lg cursor-pointer"
        />
      </div>
    );
  };

  return (
    <div
      ref={(node) => ref(drop(node))}
      className="relative flex flex-col items-center p-2 border rounded-lg bg-gray-100 w-32 h-32 overflow-hidden"
    >
      <button
        type="button"
        className="absolute top-2 right-2 z-20 bg-white text-customGreenColor rounded-full p-[2px] hover:bg-red-100 shadow-sm"
        onClick={() => {
          if (
            fileType === FILE_TYPE_ID.VIDEO &&
            statusId === VIDEO_STATUS.VIDEO_IN_PROCESS
          ) {
            toast({
              title: "Error",
              description: "You cannot delete a video in process. Please wait.",
            });
            return;
          }
          removeImage(index);
        }}
      >
        <RiDeleteBinLine className="text-red-600 box-border" />
      </button>

      {fileType === FILE_TYPE_ID.VIDEO && (
        <div className="absolute inset-0" onClick={handlePreviewClick}>
          <HLSPlayer
            src={preview}
            className="w-full h-full object-cover rounded-lg "
          />
          <div className="absolute inset-0 z-10 flex items-center justify-center">
            <div className="w-10 h-10 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
              <FaPlay className="text-white text-lg ml-1 cursor-pointer" />
            </div>
          </div>
        </div>
      )}

      <div
        className={`absolute inset-0 ${
          uploadProgress?.status === "uploading" ? "opacity-50" : ""
        }`}
        onClick={handlePreviewClick}
      >
        {fileType === FILE_TYPE_ID.VIDEO ? (
          <>
            <video className="w-full h-full object-cover" src={preview}>
              Your browser does not support the video tag.
            </video>
            {uploadProgress?.status === "uploading" && (
              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                <span className="text-white text-sm">
                  Uploading... {Math.round(uploadProgress.progress)}%
                </span>
              </div>
            )}
          </>
        ) : (
          <img
            src={preview}
            alt="Uploaded preview"
            className="w-full h-full object-cover rounded-lg cursor-pointer"
          />
        )}
      </div>

      <VideoPreviewModal
        videoSrc={filePath}
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        fileType={fileType}
      />
    </div>
  );
};

export default MediaItem;
